import { useId, useState } from 'react'
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'
import { screenPath } from '@commerce/core'
import { YALO_SEMANTIC } from '@commerce/tokens'
import { Screen } from '../design'
import { CreateSessionError, useCreateSession } from '../hooks/useCreateSession'
import { useStorefrontConfig } from '../config/StorefrontConfigContext'

interface LoginProblem {
  /** Unset for a problem that is not about the field — a failed session. */
  field?: 'storeCode'
  message: string
}

const EMPTY_CODE_PROBLEM = 'Ingresa el código de tu tienda.'

/** The client system said no — a code it does not recognise. */
const REJECTED_PROBLEM =
  'No encontramos una tienda con ese código. Revísalo e intenta de nuevo.'
const SESSION_PROBLEM = 'No pudimos iniciar tu sesión. Intenta de nuevo.'

const HINT = 'Entrarás como la tienda registrada con este código.'

/**
 * The welcome screen, reachable two ways that differ in who owns the session.
 *
 * From a chat, the channel minted the session and put it in the URL — the
 * route is `/:slug/:sessionId/login` — so entering only carries it forward.
 * From the bare `/:slug` home there is no session yet, and entering is what
 * creates one: `createSession` against the User API, with the customer
 * resolved live by the client's own system through the CREATE_SESSION add-on.
 * A repeated store code *reuses* its session — cart and selections intact —
 * which is the API's intended behaviour.
 *
 * Login is by store code alone for now. The code's format is tenant-specific,
 * so nothing beyond "not empty" is checked locally — *authorisation* is the
 * client system's call.
 */
export const LoginPage = () => {
  const { slug = '', sessionId } = useParams()
  const navigate = useNavigate()
  const { createSession, loading } = useCreateSession()
  const { logoUrl, storeName } = useStorefrontConfig()

  const [storeCode, setStoreCode] = useState('')
  const [problem, setProblem] = useState<LoginProblem | null>(null)

  const storeCodeId = useId()
  const problemId = useId()

  const enterStore = async (event: React.FormEvent) => {
    event.preventDefault()
    if (storeCode.trim() === '') {
      setProblem({ field: 'storeCode', message: EMPTY_CODE_PROBLEM })
      return
    }
    setProblem(null)

    try {
      const session = sessionId ?? (await createSession(slug, storeCode.trim()))
      // Straight to the catalog. The distribution-centre screen still exists
      // at its own path for whoever links there, but the flow no longer stops
      // by it — the "not connected" state is not part of the happy path.
      navigate(screenPath('catalog', { slug, sessionId: session }))
    } catch (error) {
      const rejected = error instanceof CreateSessionError && error.reason === 'rejected'
      setProblem({ message: rejected ? REJECTED_PROBLEM : SESSION_PROBLEM })
    }
  }

  return (
    <Screen testId="login-page">
      <Box
        component="form"
        onSubmit={enterStore}
        noValidate
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2.25,
          px: 3.5,
          py: 4.5,
        }}
      >
        {logoUrl ? (
          <Box
            component="img"
            src={logoUrl}
            alt={storeName}
            sx={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'contain', flex: 'none' }}
          />
        ) : (
          <Box
            aria-hidden
            sx={{
              width: 96,
              height: 96,
              borderRadius: '50%',
              flex: 'none',
              bgcolor: YALO_SEMANTIC.bgSunken,
              color: YALO_SEMANTIC.fg3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 34,
              fontWeight: 700,
            }}
          >
            {storeName.trim().charAt(0).toUpperCase()}
          </Box>
        )}

        <Stack spacing={0.75} sx={{ textAlign: 'center' }}>
          <Typography variant="h2" component="h1">
            ¡Bienvenido!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Entra a la tienda de tu distribuidor
          </Typography>
        </Stack>

        <Stack spacing={1.75} sx={{ width: '100%' }}>
          <Stack spacing={0.875}>
            {/* The design labels its fields with an eyebrow above them rather
                than MUI's floating label, so the label is a real `<label>`
                element wired by `htmlFor` instead of the `label` prop. */}
            <Typography component="label" htmlFor={storeCodeId} variant="overline">
              Código de tienda
            </Typography>
            <TextField
              id={storeCodeId}
              value={storeCode}
              onChange={(event) => {
                setStoreCode(event.target.value)
                setProblem(null)
              }}
              autoComplete="off"
              spellCheck={false}
              error={problem?.field === 'storeCode'}
              // The message renders as an alert below the button, where the
              // design puts it, so the field has to point at it explicitly —
              // otherwise a screen reader on the input hears only "invalid".
              slotProps={{
                htmlInput: problem?.field === 'storeCode' ? { 'aria-describedby': problemId } : {},
              }}
              sx={{
                '& .MuiInputBase-input': {
                  height: 50,
                  boxSizing: 'border-box',
                  textAlign: 'center',
                  fontSize: 16,
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                },
              }}
            />
          </Stack>

          {/* Disabled only while the request is in flight — not a validation
              dead end, just double-submit protection. */}
          <Button type="submit" variant="contained" size="large" fullWidth disabled={loading}>
            {loading ? 'Entrando…' : 'Entrar'}
          </Button>

          {problem && (
            <Alert id={problemId} severity="error">
              {problem.message}
            </Alert>
          )}

          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
            {HINT}
          </Typography>
        </Stack>
      </Box>
    </Screen>
  )
}
